import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { toast } from 'react-toastify';
import {
  PageHeader,
  DataTable,
  StatusBadge,
  ActionMenu,
  ConfirmDialog,
  StatCard,
  EmptyState,
} from '../../components/ui';
import type { Column, ActionItem } from '../../components/ui';
import { servicesApi } from '../../api/services';
import { formatDate } from '../../utils';
import type { AccountStatus, ServiceCategory, Subcategory } from '../../types';

interface SubForm {
  name: string;
  description: string;
  status: AccountStatus;
}

const emptySubForm: SubForm = {
  name: '',
  description: '',
  status: 'Active',
};

const CategoryDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<ServiceCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingSub, setEditingSub] = useState<Subcategory | null>(null);
  const [form, setForm] = useState<SubForm>(emptySubForm);
  const [confirm, setConfirm] = useState<{
    subcategory: Subcategory;
    status: AccountStatus;
  } | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await servicesApi.getCategoryById(id);
      setCategory(data);
    } catch {
      toast.error('Category not found');
      navigate('/categories');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingSub(null);
    setForm(emptySubForm);
    setDialogOpen(true);
  };

  const openEdit = (sub: Subcategory) => {
    setEditingSub(sub);
    setForm({
      name: sub.name,
      description: sub.description,
      status: sub.status,
    });
    setDialogOpen(true);
  };

  const handleSaveSub = async () => {
    if (!category) return;
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editingSub) {
        await servicesApi.editSubcategory(editingSub.id, form.name.trim());
        toast.success('Subcategory updated');
      } else {
        if (!category.serviceId) {
          throw new Error('Service ID missing for this category');
        }
        await servicesApi.createSubcategory({
          serviceID: category.serviceId,
          categoryID: category.id,
          name: form.name.trim(),
        });
        toast.success('Subcategory created');
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save subcategory');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async () => {
    if (!category || !confirm) return;
    setConfirmLoading(true);
    try {
      toast.info('Subcategory status API is not available yet');
      setConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const columns: Column<Subcategory>[] = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        minWidth: 180,
        render: (row) => (
          <Typography fontWeight={600} fontSize={14}>
            {row.name}
          </Typography>
        ),
      },
      {
        id: 'description',
        label: 'Description',
        minWidth: 220,
        render: (row) => (
          <Typography variant="body2" color="text.secondary">
            {row.description || '—'}
          </Typography>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        id: 'created',
        label: 'Created',
        render: (row) => formatDate(row.createdAt),
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'right',
        render: (row) => {
          const actions: ActionItem[] = [
            {
              label: 'Edit',
              icon: <EditIcon fontSize="small" />,
              onClick: () => openEdit(row),
            },
            row.status === 'Active'
              ? {
                  label: 'Deactivate',
                  icon: <BlockIcon fontSize="small" />,
                  danger: true,
                  onClick: () => setConfirm({ subcategory: row, status: 'Inactive' }),
                }
              : {
                  label: 'Activate',
                  icon: <CheckCircleOutlineIcon fontSize="small" />,
                  onClick: () => setConfirm({ subcategory: row, status: 'Active' }),
                },
          ];
          return <ActionMenu actions={actions} />;
        },
      },
    ],
    []
  );

  if (loading || !category) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={category.name}
        subtitle={category.description}
        breadcrumbs={[
          { label: 'Categories', to: '/categories' },
          { label: category.name },
        ]}
        actions={<StatusBadge status={category.status} />}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Providers"
            value={category.providerCount}
            icon={<PeopleOutlineIcon />}
            accent="#1E3A8A"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Requests"
            value={category.requestCount}
            icon={<AssignmentOutlinedIcon />}
            accent="#10B981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Active Jobs"
            value={category.activeJobs}
            icon={<WorkOutlineIcon />}
            accent="#F59E0B"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Completed Jobs"
            value={category.completedJobs}
            icon={<TaskAltIcon />}
            accent="#059669"
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h3">Subcategories</Typography>
        <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={openCreate}>
          Add Subcategory
        </Button>
      </Box>

      {category.subcategories.length === 0 ? (
        <EmptyState
          title="No subcategories"
          description="Add a subcategory to organize services under this category."
          action={
            <Button variant="contained" color="secondary" startIcon={<AddIcon />} onClick={openCreate}>
              Add Subcategory
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={category.subcategories}
          getRowId={(row) => row.id}
        />
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSub ? 'Edit Subcategory' : 'Add Subcategory'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            fullWidth
            required
            margin="dense"
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, status: e.target.value as AccountStatus }))
              }
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="secondary" onClick={handleSaveSub} disabled={saving}>
            {editingSub ? 'Save Changes' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.status === 'Active' ? 'Activate Subcategory' : 'Deactivate Subcategory'}
        message={
          confirm
            ? `Are you sure you want to ${confirm.status === 'Active' ? 'activate' : 'deactivate'} “${confirm.subcategory.name}”?`
            : ''
        }
        confirmLabel={confirm?.status === 'Active' ? 'Activate' : 'Deactivate'}
        danger={confirm?.status === 'Inactive'}
        loading={confirmLoading}
        onConfirm={handleStatusChange}
        onClose={() => !confirmLoading && setConfirm(null)}
      />
    </Box>
  );
};

export default CategoryDetailPage;
